/**
 * JWT session helpers: decode expiry, clear storage, logout redirect.
 */

import { removeJwtToken, removeUserData } from './localStorage'

let logoutRedirectInFlight = false

/** Decode standard JWT payload (middle segment). Returns null if invalid. */
export function parseJwtPayload(token: string): { exp?: number; iat?: number } | null {
	try {
		const parts = token.split('.')
		if (parts.length < 2) return null
		const base64Url = parts[1]
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
		const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
		const json = atob(padded)
		return JSON.parse(json) as { exp?: number; iat?: number }
	} catch {
		return null
	}
}

/**
 * True if token is missing, malformed, or `exp` is at or before now (Unix seconds).
 */
export function isJwtExpired(token: string | null): boolean {
	if (!token || token.length === 0) return true
	const payload = parseJwtPayload(token)
	if (!payload) return true
	if (typeof payload.exp !== 'number') return false
	const nowSec = Math.floor(Date.now() / 1000)
	return payload.exp <= nowSec
}

export function clearAuthSession(): void {
	removeJwtToken()
	removeUserData()
}

const LOGIN_PATH_SEGMENT = 'auth-pages/login'

function isLoginRoute(): boolean {
	return window.location.pathname.includes(LOGIN_PATH_SEGMENT)
}

/**
 * Clears stored auth and sends the user to the login page (unless already there).
 */
export function logoutAndRedirectToLogin(): void {
	if (logoutRedirectInFlight) return
	clearAuthSession()
	if (isLoginRoute()) return
	logoutRedirectInFlight = true
	window.location.assign(`/${LOGIN_PATH_SEGMENT}`)
}
